import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  RefreshCw, Loader2, History, Trash2, RotateCcw, ShieldAlert, AlertTriangle, Mail, User,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function RestoreDialog({ open, onOpenChange, account, onRestored }) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setReason(''); setConfirmed(false); }
  }, [open]);

  if (!account) return null;

  const handleRestore = async () => {
    if (reason.trim().length < 10) {
      toast.error('Raison trop courte (10 caractères min.)');
      return;
    }
    if (!confirmed) {
      toast.error('Cochez la confirmation');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/account-recovery/restore`,
        { target_user_id: account.user_id, reason: reason.trim(), confirmed: true },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(res.data.message || 'Compte restauré');
      onRestored?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la restauration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-emerald-600" /> Restaurer le compte
          </DialogTitle>
          <DialogDescription>
            L&apos;email originel sera rétabli et le compte réactivé.
            L&apos;utilisateur devra utiliser «&nbsp;Mot de passe oublié&nbsp;» pour fixer un nouveau mot de passe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
            <div>• Email à restaurer : <strong>{account.email_original}</strong></div>
            <div>• Utilisateur : <strong>{account.prenom} {account.nom}</strong> ({account.role})</div>
            <div>• Libéré le : <strong>{formatDate(account.released_at)}</strong> par {account.released_by}</div>
            <div>• Données métier conservées : {account.assets_count?.documents || 0} doc, {account.assets_count?.souscriptions || 0} sous., {account.assets_count?.adhesions || 0} adh.</div>
          </div>
          <div>
            <label className="text-sm font-semibold">Raison de la restauration <span className="text-rose-600">*</span></label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Restauration suite à demande / erreur de libération..."
              rows={2}
              className={`mt-1 ${reason.trim().length > 0 && reason.trim().length < 10 ? 'border-amber-400' : ''}`}
              data-testid="restore-reason-input"
            />
            <p className={`text-xs mt-1 ${reason.trim().length < 10 ? 'text-amber-700 font-medium' : 'text-emerald-700'}`}>
              {reason.trim().length}/10 caractères min.
            </p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox checked={confirmed} onCheckedChange={setConfirmed} className="mt-0.5" data-testid="restore-confirm" />
            <span className="text-sm">Je confirme vouloir restaurer cet email et l&apos;accès du compte.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={handleRestore}
            disabled={loading || !confirmed || reason.trim().length < 10}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
            data-testid="restore-execute-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
            Restaurer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReleasedAccountsAdmin() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [showOnlyRestorable, setShowOnlyRestorable] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/account-recovery/released-accounts?limit=500`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAccounts(res.data.accounts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const filtered = accounts.filter((a) => {
    if (showOnlyRestorable && !a.can_restore) return false;
    const f = filter.trim().toLowerCase();
    if (!f) return true;
    return (
      (a.email_original || '').toLowerCase().includes(f) ||
      (a.email_current || '').toLowerCase().includes(f) ||
      (a.nom || '').toLowerCase().includes(f) ||
      (a.prenom || '').toLowerCase().includes(f) ||
      (a.released_by || '').toLowerCase().includes(f)
    );
  });

  const restorableCount = accounts.filter((a) => a.can_restore).length;
  const conflictCount = accounts.filter((a) => !a.can_restore).length;

  return (
    <div className="space-y-4" data-testid="released-accounts-admin">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-xl">
            <History className="w-5 h-5 text-rose-600" />
            Comptes libérés
          </CardTitle>
          <CardDescription>
            Visualisez et restaurez les comptes dont l&apos;email a été libéré (purge complète).
            Action sensible — journalisée dans l&apos;audit.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2 flex-1 min-w-[220px]">
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtrer par email, nom, libéré par..."
                className="max-w-md"
                data-testid="released-filter-input"
              />
            </div>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <Checkbox checked={showOnlyRestorable} onCheckedChange={setShowOnlyRestorable} data-testid="released-only-restorable" />
              Seulement restaurables
            </label>
            <Button variant="outline" size="sm" onClick={fetchAccounts} disabled={loading} data-testid="released-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Actualiser
            </Button>
          </div>

          <div className="flex gap-2 mb-3 text-xs flex-wrap">
            <Badge className="bg-rose-700 text-white border-0 shadow-sm font-semibold" data-testid="released-total-badge">
              <Trash2 className="w-3 h-3 mr-1" /> {accounts.length} comptes libérés
            </Badge>
            <Badge className="bg-emerald-700 text-white border-0 shadow-sm font-semibold" data-testid="released-restorable-badge">
              <RotateCcw className="w-3 h-3 mr-1" /> {restorableCount} restaurables
            </Badge>
            {conflictCount > 0 && (
              <Badge className="bg-amber-700 text-white border-0 shadow-sm font-semibold" data-testid="released-conflict-badge">
                <AlertTriangle className="w-3 h-3 mr-1" /> {conflictCount} en conflit (email ré-utilisé)
              </Badge>
            )}
          </div>

          {loading && accounts.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              Chargement...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
              <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <div className="font-semibold text-slate-500">Aucun compte libéré</div>
              <div className="text-xs">
                {filter ? 'Aucun résultat pour ce filtre' : 'Quand vous libérerez un email, il apparaîtra ici.'}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm" data-testid="released-accounts-table">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Email originel</th>
                    <th className="text-left px-3 py-2 font-semibold">Utilisateur</th>
                    <th className="text-left px-3 py-2 font-semibold">Libéré le</th>
                    <th className="text-left px-3 py-2 font-semibold">Par</th>
                    <th className="text-center px-3 py-2 font-semibold">Données</th>
                    <th className="text-right px-3 py-2 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr
                      key={a.user_id}
                      className="border-t border-slate-200 hover:bg-slate-50/60 transition-colors"
                      data-testid={`released-row-${a.user_id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <span className="font-mono text-xs">{a.email_original || <em className="text-slate-400">non enregistré</em>}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[280px]" title={a.email_current}>
                          {a.email_current}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-medium">{a.prenom} {a.nom}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px] mt-0.5">{a.role || '—'}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                        {formatDate(a.released_at)}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 font-mono truncate max-w-[180px]" title={a.released_by}>
                        {a.released_by || '—'}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-slate-500 whitespace-nowrap">
                        <span title="Documents">📄 {a.assets_count?.documents || 0}</span>
                        {' · '}
                        <span title="Souscriptions">📋 {a.assets_count?.souscriptions || 0}</span>
                        {' · '}
                        <span title="Adhésions">🎫 {a.assets_count?.adhesions || 0}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {a.can_restore ? (
                          <Button
                            size="sm"
                            onClick={() => setRestoreTarget(a)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`released-restore-btn-${a.user_id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurer
                          </Button>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] text-amber-700 font-medium"
                            title={`Email ré-utilisé par le compte ${a.conflict_with_user_id || '?'}`}
                            data-testid={`released-conflict-${a.user_id}`}
                          >
                            <ShieldAlert className="w-3.5 h-3.5" /> Conflit
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RestoreDialog
        open={!!restoreTarget}
        onOpenChange={(o) => { if (!o) setRestoreTarget(null); }}
        account={restoreTarget}
        onRestored={fetchAccounts}
      />
    </div>
  );
}
